import { LiveAnnouncer } from '@angular/cdk/a11y';
import { inject, Injectable } from '@angular/core';
import type { OutOfStockItem, ProductCart, ProductDetailsCart } from '@models';
import { SnackbarService, STOCK_MESSAGES, stockProductNames } from '@shared';

import { CartDataService } from './cart-data.service';

/**
 * RCP-458 — products can be sold only up to their available count (RCP-456 DoD10–DoD13).
 *
 * `GET cart` gives each line the SKU's `availableCount` and flags the lines above it
 * (`exceedsAvailableStock`), so the "+" and "Add to cart" are bounded before any request. The
 * backend still owns the check: `cart/add`, `cart/update` and `orders` answer `409` with the failing
 * products when stock moved in the meantime, and the callers refetch the cart to pick up the new
 * count. The count bounds the controls only — it is never shown to the player (DoD13).
 */
@Injectable({ providedIn: 'root' })
export class StockLimitService {
  private readonly cartDataService = inject(CartDataService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly liveAnnouncer = inject(LiveAnnouncer);

  /**
   * Stepper cap for a cart line: its own quantity while it is above stock, else the line's share of
   * the SKU's available count. The count is a SKU total (the backend sums all lines), so a SKU spread
   * over several lines (pre-RCP-379 Apple carts) leaves each line only the headroom the others don't use.
   */
  capFor(product: ProductCart): number | undefined {
    if (product.exceedsAvailableStock) return product.quantity;
    if (product.availableCount === undefined) return undefined;
    return (
      product.quantity + Math.max(0, product.availableCount - this.cartDataService.quantityForSku(product.productId))
    );
  }

  /**
   * The cart already holds all the available units of this SKU, so "Add to cart" would be refused.
   * A SKU not in the cart is never capped here: at zero stock the product is inactive and shown as
   * out of stock, and a race is answered by the backend's `409`.
   */
  isSkuStockCapped(productId: number | null | undefined): boolean {
    if (productId == null) return false;
    const availableCount = this.cartDataService
      .productDetailsCart()
      ?.products.find((product) => product.productId === productId)?.availableCount;
    return availableCount !== undefined && this.cartDataService.quantityForSku(productId) >= availableCount;
  }

  /** DoD10 — top error snackbar naming the products that could not be added / raised. */
  notifyOutOfStock(items: Pick<OutOfStockItem, 'productId' | 'name'>[]): void {
    const cartProducts = this.cartDataService.productDetailsCart()?.products ?? [];
    this.notify(STOCK_MESSAGES.snackbarTitle, STOCK_MESSAGES.addOrRaise(stockProductNames(items, cartProducts)));
  }

  /** DoD11 / DoD12 — checkout is blocked: top error snackbar naming each failing product. */
  notifyCheckoutBlocked(products: Pick<ProductCart, 'productId' | 'name'>[]): void {
    this.notify(STOCK_MESSAGES.blockedTitle, STOCK_MESSAGES.blockedList(stockProductNames(products)));
  }

  /**
   * The snackbar is not a live region, so the error is also announced (WCAG 2.1 AA — errors must
   * reach screen-reader users, not only be painted).
   */
  private notify(title: string, message: string): void {
    this.snackbarService.error(title, message);
    void this.liveAnnouncer.announce(`${title}. ${message}`, 'assertive');
  }

  /** DoD11 — cart lines that block checkout: no longer available, or above the available count. */
  stockIssues(cart: ProductDetailsCart | null): ProductCart[] {
    return (cart?.products ?? []).filter((product) => !product.isAvailable || product.exceedsAvailableStock);
  }
}
